import { ReactNode } from 'react';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiPencilLine,
} from '@remixicon/react';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';

interface ExternalUrlArtifactListItemProps {
  artifact: AttachmentFragment;
  icon: ReactNode;
  subtitleLabel: string;
  openLabel: string;
  /** Optional UI URL. Omit it when the artifact has no navigable UI. */
  href?: string;
  createTimeFormat?: string;
  fallbackTitle?: (artifact: AttachmentFragment) => string;
  onDelete?: () => void;
  onRename?: () => void;
}

const defaultFallbackTitle = (artifact: AttachmentFragment) => artifact.uri;

export const ExternalUrlArtifactListItem = ({
  artifact,
  icon,
  subtitleLabel,
  openLabel,
  href,
  createTimeFormat = DateUtils.MONTH_DAY_FORMAT,
  fallbackTitle = defaultFallbackTitle,
  onDelete,
  onRename,
}: ExternalUrlArtifactListItemProps) => {
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    createTimeFormat,
  );

  const safeUrl = href && isSafeUrl(href) ? href : undefined;

  const open = () => {
    if (safeUrl) {
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root onClick={safeUrl ? open : undefined}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={artifact.displayName || fallbackTitle(artifact)}
        secondary={
          <>
            {subtitleLabel} &middot; {dateLabel}
          </>
        }
      />
      {(safeUrl || onRename || onDelete) && (
        <ArtifactListItem.Action>
          <ArtifactMenu.Root aria-label="Artifact actions">
            {safeUrl && (
              <ArtifactMenu.Item
                textValue={openLabel}
                icon={<RiExternalLinkLine size={16} />}
                onClick={open}
              >
                {openLabel}
              </ArtifactMenu.Item>
            )}
            {onRename && (
              <ArtifactMenu.Item
                textValue="Rename"
                icon={<RiPencilLine size={16} />}
                onClick={onRename}
              >
                Rename
              </ArtifactMenu.Item>
            )}
            {onDelete && (
              <ArtifactMenu.Item
                textValue="Delete"
                icon={<RiDeleteBinLine size={16} />}
                onClick={onDelete}
                destructive
              >
                Delete
              </ArtifactMenu.Item>
            )}
          </ArtifactMenu.Root>
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
